import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import api from './services/api';
import AdminLogin from './pages/Login';
import UserManagement from './components/UserManagement';
import NoticeForm from './components/NoticeForm';
import BookingApprovals from './components/BookingApprovals';
import PollManager from './components/PollManager';
import ProcurementManager from './components/ProcurementManager';
import GroupBuyManager from './components/GroupBuyManager';
import TicketKanban from './components/TicketKanban';
import DocumentUpload from './components/DocumentUpload';
import SocietySettings from './components/SocietySettings';
import PLManager from './components/PLManager';
import FacilityManager from './components/FacilityManager';
import PermissionsManager from './components/PermissionsManager';
import NotificationCenter from './components/NotificationCenter';
import {
  Users,
  Bell,
  CalendarCheck,
  PieChart,
  FileText,
  Package,
  MessageSquare,
  Settings,
  LayoutDashboard,
  LogOut,
  ChevronRight,
  ShieldCheck,
  Search,
  Menu,
  X,
  PlusCircle,
  CreditCard,
  ShieldAlert,
  FolderOpen,
  Building2,
  Megaphone,
  Lock
} from 'lucide-react';
import { cn } from './lib/utils';

const Sidebar = ({ isOpen, setIsOpen, profile }: { isOpen: boolean, setIsOpen: (o: boolean) => void, profile: any }) => {
  const location = useLocation();
  const isSupport = profile?.role === 'support';

  const navGroups = [
    {
      label: 'OPERATIONS',
      items: [
        { name: 'Dashboard', path: '/', icon: LayoutDashboard },
        { name: 'Users & Units', path: '/users', icon: Users, adminOnly: true },
        { name: 'Facilities', path: '/facilities', icon: Building2, adminOnly: true },
        { name: 'Notices & MoMs', path: '/notices', icon: Bell, adminOnly: true },
        { name: 'Broadcaster', path: '/notify', icon: Megaphone, adminOnly: true },
      ]
    },
    {
      label: 'COMMUNITY',
      items: [
        { name: 'Facility Bookings', path: '/bookings', icon: CalendarCheck, adminOnly: true },
        { name: 'Poll Center', path: '/polls', icon: PieChart, adminOnly: true },
        { name: 'Group Buys', path: '/group-buys', icon: Package, adminOnly: true },
      ]
    },
    {
      label: 'FINANCE',
      items: [
        { name: 'Procurement', path: '/procurement', icon: FileText, adminOnly: true },
        { name: 'P&L Statements', path: '/pl-statements', icon: CreditCard, adminOnly: true },
      ]
    },
    {
      label: 'SUPPORT',
      items: [
        { name: 'Ticket Kanban', path: '/tickets', icon: MessageSquare },
        { name: 'Resident Handbook', path: '/documents', icon: FolderOpen, adminOnly: true },
      ]
    }
  ];

  const filteredGroups = navGroups.map(group => ({
    ...group,
    items: group.items.filter(item => !isSupport || !item.adminOnly)
  })).filter(group => group.items.length > 0);

  return (
    <>
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-[#0F172A] text-slate-400 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-auto border-r border-slate-800",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="h-full flex flex-col p-4">
          <div className="flex items-center gap-3 mb-8 px-2">
            <div className="w-10 h-10 bg-violet-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-violet-600/20">
              <ShieldCheck size={24} />
            </div>
            <div>
              <h1 className="font-bold text-white text-lg tracking-tight leading-none">Society Mitra</h1>
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Admin Panel</p>
            </div>
            <button onClick={() => setIsOpen(false)} className="lg:hidden ml-auto p-2 hover:bg-slate-800 rounded-xl">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-6">
            {filteredGroups.map((group) => (
              <div key={group.label}>
                <p className="text-[10px] font-bold text-slate-600 tracking-[0.2em] mb-3 px-4">{group.label}</p>
                <nav className="space-y-1">
                  {group.items.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className={cn(
                          "flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 text-sm",
                          isActive
                            ? "bg-slate-800 text-white font-medium"
                            : "hover:bg-slate-800/50 hover:text-slate-200"
                        )}
                      >
                        <item.icon size={18} className={cn(isActive ? "text-violet-500" : "text-slate-500")} />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </nav>
              </div>
            ))}
          </div>

          <div className="pt-4 mt-4 border-t border-slate-800 space-y-1">
            {!isSupport && (
              <>
                <Link
                  to="/settings"
                  className={cn(
                    "flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 text-sm",
                    location.pathname === '/settings' ? "bg-slate-800 text-white" : "hover:bg-slate-800/50 hover:text-slate-200"
                  )}
                >
                  <Settings size={18} />
                  <span>Settings</span>
                </Link>
                <Link
                  to="/permissions"
                  className={cn(
                    "flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 text-sm",
                    location.pathname === '/permissions' ? "bg-slate-800 text-white" : "hover:bg-slate-800/50 hover:text-slate-200"
                  )}
                >
                  <Lock size={18} />
                  <span>Access Control</span>
                </Link>
              </>
            )}
            <button
              onClick={() => { localStorage.removeItem('token'); window.location.href = '/login'; }}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-500 transition-colors text-sm"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

const ProtectedRoute = ({ children, adminOnly = false, profile }: any) => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  if (profile && adminOnly && profile.role !== 'admin') return <Navigate to="/" />;
  return children;
};

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get('/profiles/me');
        setProfile(res.data);
        if (res.data.role === 'admin') {
          const statsRes = await api.get('/admin/dashboard/stats');
          setStats(statsRes.data);
        }
      } catch (err) {
        console.error(err);
        localStorage.removeItem('token');
        window.location.href = '/login';
      } finally {
        setLoading(false);
      }
    };
    if (localStorage.getItem('token')) fetchProfile();
    else setLoading(false);
  }, []);

  if (loading) return <div className="h-screen flex items-center justify-center text-slate-400 font-medium">Loading Society Mitra...</div>;

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<AdminLogin />} />
        <Route path="*" element={
          <ProtectedRoute profile={profile}>
            <div className="min-h-screen bg-[#F8FAFC] lg:flex font-sans text-slate-900 overflow-hidden">
              <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} profile={profile} />

              <div className="flex-1 flex flex-col h-screen overflow-hidden">
                <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between z-40">
                  <div className="flex items-center gap-4">
                    <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-xl">
                      <Menu size={20} />
                    </button>
                    <div className="text-sm font-medium text-slate-500">
                      Society Mitra / <span className="text-slate-900 capitalize">{window.location.pathname.split('/')[1] || 'Dashboard'}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                     <div className="text-right hidden sm:block">
                        <p className="text-sm font-semibold text-slate-900">{profile?.society_name || profile?.society_id?.toString().slice(0,8) || 'Society'}</p>
                        <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">{profile?.role}</p>
                     </div>
                     <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-white font-bold text-xs">
                        {profile?.name?.charAt(0) || 'A'}
                     </div>
                  </div>
                </header>

                <main className="flex-1 overflow-y-auto p-6">
                  <div className="max-w-7xl mx-auto">
                    <Routes>
                      <Route path="/" element={
                        <div className="space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <StatCard label="Total Residents" value={stats?.total_residents || '0'} icon={Users} />
                            <StatCard label="Pending Bookings" value={stats?.pending_bookings || '0'} icon={CalendarCheck} />
                            <StatCard label="Open Tickets" value={stats?.open_tickets || '0'} icon={MessageSquare} />
                            <StatCard label="Active Polls" value={stats?.active_polls || '0'} icon={PieChart} />
                          </div>
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
                             <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6 min-h-[300px]">
                                <h3 className="text-lg font-bold mb-4">Recent Activity</h3>
                                <div className="text-slate-400 text-sm italic">Live activity feed coming soon...</div>
                             </div>
                             <div className="bg-white rounded-xl border border-slate-200 p-6">
                                <h3 className="text-lg font-bold mb-4">Quick Actions</h3>
                                <div className="space-y-2">
                                   <Link to="/notify" className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors text-sm font-medium">
                                      <PlusCircle size={18} className="text-violet-500" />
                                      Post New Notice
                                   </Link>
                                   <Link to="/polls" className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors text-sm font-medium">
                                      <PieChart size={18} className="text-blue-500" />
                                      Create Poll
                                   </Link>
                                   <Link to="/users" className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors text-sm font-medium">
                                      <Users size={18} className="text-emerald-500" />
                                      Add New Resident
                                   </Link>
                                </div>
                             </div>
                          </div>
                        </div>
                      } />
                      <Route path="/users" element={<ProtectedRoute adminOnly={true} profile={profile}><UserManagement /></ProtectedRoute>} />
                      <Route path="/notices" element={<ProtectedRoute adminOnly={true} profile={profile}><NoticeForm /></ProtectedRoute>} />
                      <Route path="/bookings" element={<ProtectedRoute adminOnly={true} profile={profile}><BookingApprovals /></ProtectedRoute>} />
                      <Route path="/polls" element={<ProtectedRoute adminOnly={true} profile={profile}><PollManager /></ProtectedRoute>} />
                      <Route path="/procurement" element={<ProtectedRoute adminOnly={true} profile={profile}><ProcurementManager /></ProtectedRoute>} />
                      <Route path="/group-buys" element={<ProtectedRoute adminOnly={true} profile={profile}><GroupBuyManager /></ProtectedRoute>} />
                      <Route path="/tickets" element={<TicketKanban />} />
                      <Route path="/settings" element={<ProtectedRoute adminOnly={true} profile={profile}><SocietySettings /></ProtectedRoute>} />
                      <Route path="/documents" element={<ProtectedRoute adminOnly={true} profile={profile}><DocumentUpload /></ProtectedRoute>} />
                      <Route path="/pl-statements" element={<ProtectedRoute adminOnly={true} profile={profile}><PLManager /></ProtectedRoute>} />
                      <Route path="/facilities" element={<ProtectedRoute adminOnly={true} profile={profile}><FacilityManager /></ProtectedRoute>} />
                      <Route path="/permissions" element={<ProtectedRoute adminOnly={true} profile={profile}><PermissionsManager /></ProtectedRoute>} />
                      <Route path="/notify" element={<ProtectedRoute adminOnly={true} profile={profile}><NotificationCenter /></ProtectedRoute>} />
                    </Routes>
                  </div>
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  );
}

const StatCard = ({ label, value, icon: Icon }: any) => {
  return (
    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
          <Icon size={20} />
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
          <p className="text-xl font-bold text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

export default App;
